import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  completePassageExecution,
  completeTourneeExecution,
  failPassageExecution,
  pauseTourneeExecution,
  reportPassageIncident,
  resumeTourneeExecution,
  startPassageExecution,
  startTourneeExecution,
} from "./execution.service";
import type { PassageIncidentType } from "../types/execution.types";

type PendingKey =
  | "startTournee"
  | "pauseTournee"
  | "resumeTournee"
  | "completeTournee"
  | "startPassage"
  | "completePassage"
  | "failPassage"
  | "reportIncident";

const CODE_TO_MESSAGE: Record<string, string> = {
  ANOTHER_PASSAGE_RUNNING: "Un autre passage est déjà en cours",
  TOURNEE_NOT_RUNNING: "La tournée doit être en cours",
  PASSAGE_NOT_ASSIGNED_TO_TOURNEE: "Passage non affecté à une tournée",
  PASSAGE_NOT_FOUND: "Passage introuvable",
  PASSAGE_BLOCKED_BY_PROVISION: "Provision insuffisante — créditez la provision ou contactez l’administration",
  PASSAGE_BLOCKED_BY_PREVIOUS: "Ordre des passages : un créneau antérieur bloque le démarrage",
  PASSAGE_PHOTOS_REQUIRED: "Photos avant/après obligatoires",
  PASSAGE_START_FAILED: "Démarrage impossible (erreur serveur)",
  PASSAGE_ALREADY_STARTED: "Ce passage est déjà démarré",
  CANNOT_COMPLETE_TOURNEE_WITH_RUNNING_PASSAGE: "Impossible de clôturer : un passage est en cours",
  CANNOT_COMPLETE_TOURNEE_WITH_PLANNED_PASSAGE:
    "Impossible de clôturer : des passages sont encore planifiés ou non traités.",
  CANNOT_COMPLETE_TOURNEE_CRITICAL_INCIDENT: "Impossible de clôturer : incident critique ouvert.",
  TOURNEE_CLOSURE_BLOCKED: "Clôture impossible — corrigez les points bloquants.",
  CLOSURE_REASON_REQUIRED: "Un motif est obligatoire pour clôturer avec anomalies.",
  TOURNEE_ALREADY_COMPLETED: "Action impossible sur une tournée terminée",
  TOURNEE_ALREADY_STARTED: "Tournée déjà lancée",
  PASSAGE_ALREADY_RUNNING: "Ce passage est déjà en cours",
  PASSAGE_ALREADY_COMPLETED: "Ce passage est déjà terminé",
  PASSAGE_NOT_RUNNING: "Le passage doit être en cours",
  FORBIDDEN: "Action non autorisée",
  UNAUTHORIZED: "Action non autorisée",
};

/** Messages utilisateur pour erreurs API exécution (POST start/pause/…). */
export function mapExecutionApiError(err: unknown, fallback?: string): string {
  const data = (err as { response?: { data?: { error?: string; message?: string } } })?.response?.data;
  const code = data?.error;
  const msg = typeof data?.message === "string" ? data.message.trim() : "";
  if (code && CODE_TO_MESSAGE[code]) return CODE_TO_MESSAGE[code];
  if (msg) return msg;
  if (code) return code;
  if (err instanceof Error && /Network Error|timeout|Failed to fetch/i.test(err.message)) {
    return "Connexion instable. Réessayez.";
  }
  if (fallback) return fallback;
  return "Action impossible pour le moment.";
}

function mapErrorMessage(err: unknown, fallback?: string): string {
  return mapExecutionApiError(err, fallback);
}

export function useExecutionMutations(params: {
  tourneeId?: string;
  refresh: () => Promise<void>;
}) {
  const { tourneeId, refresh } = params;
  const [pending, setPending] = useState<Record<PendingKey, boolean>>({
    startTournee: false,
    pauseTournee: false,
    resumeTournee: false,
    completeTournee: false,
    startPassage: false,
    completePassage: false,
    failPassage: false,
    reportIncident: false,
  });

  const run = async (key: PendingKey, action: () => Promise<void>, successMessage: string, errorFallback?: string) => {
    if (pending[key]) return false;
    try {
      setPending((p) => ({ ...p, [key]: true }));
      await action();
      await refresh();
      toast.success(successMessage);
      return true;
    } catch (e) {
      toast.error(mapErrorMessage(e, errorFallback));
      return false;
    } finally {
      setPending((p) => ({ ...p, [key]: false }));
    }
  };

  return useMemo(
    () => ({
      pending,
      startTournee: () =>
        tourneeId
          ? run("startTournee", () => startTourneeExecution(tourneeId), "Tournée lancée", "Impossible de lancer la tournée")
          : Promise.resolve(false),
      pauseTournee: () =>
        tourneeId
          ? run("pauseTournee", () => pauseTourneeExecution(tourneeId), "Tournée suspendue")
          : Promise.resolve(false),
      resumeTournee: () =>
        tourneeId
          ? run("resumeTournee", () => resumeTourneeExecution(tourneeId), "Tournée reprise")
          : Promise.resolve(false),
      completeTournee: async (closureReason?: string) => {
        if (!tourneeId || pending.completeTournee) return false;
        try {
          setPending((p) => ({ ...p, completeTournee: true }));
          const out = await completeTourneeExecution(tourneeId, { closureReason });
          await refresh();
          const sev = out.closure?.severity;
          const withAnomaly = sev === "WARNING" || sev === "CRITICAL";
          const msg = withAnomaly
            ? `Tournée clôturée avec anomalies (${sev === "CRITICAL" ? "critique" : "avertissement"}).`
            : out.closeWarnings?.length
              ? `Tournée clôturée — ${out.closeWarnings.join("; ")}`
              : "Tournée clôturée";
          toast.success(msg);
          return true;
        } catch (e) {
          toast.error(mapErrorMessage(e));
          return false;
        } finally {
          setPending((p) => ({ ...p, completeTournee: false }));
        }
      },
      startPassage: (passageId: number) =>
        run("startPassage", () => startPassageExecution(passageId), "Passage démarré"),
      completePassage: (passageId: number) =>
        run("completePassage", () => completePassageExecution(passageId), "Passage terminé"),
      failPassage: (passageId: number) =>
        run("failPassage", () => failPassageExecution(passageId), "Passage marqué en échec"),
      reportIncident: (passageId: number, payload: { type: PassageIncidentType; description: string }) =>
        run("reportIncident", () => reportPassageIncident(passageId, payload), "Incident enregistré"),
      mapErrorMessage,
    }),
    [pending, refresh, tourneeId]
  );
}

